import classNames from "classnames";
import React, { ReactNode } from "react";
import styles from "./styles.module.scss";
import { TextElements } from "src/utils/types";
import { IDS, TEXT_TAGS } from "src/utils/enums";
import { useAppSelector } from "src/hooks/useRedux";
import { useIsMobile } from "src/hooks/useIsMobile";
interface UITextProps {
  id?: IDS;
  size: number;
  weight: number;
  style?: string;
  text: ReactNode;
  isAriaHidden?: boolean;
  tag?: TextElements;
  ariaLabel?: string;
  announce?: boolean;
  politeness?: "polite" | "assertive";
  tabIndex?: number;
}

const UIText: React.FC<UITextProps> = ({
  id,
  size,
  weight,
  style,
  text,
  isAriaHidden = false,
  tag = TEXT_TAGS.SPAN,
  ariaLabel,
  announce = false,
  politeness = "polite",
  tabIndex,
}) => {
  const { strings, showStringKeys } = useAppSelector((state) => state.app);
  const isMobile = useIsMobile();

  // Check if we're in a development environment


  // const processText = (content: string) => {
  //   const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/g;
  //   return content.replace(urlRegex, (url) => {
  //     const fullUrl = url.startsWith("www.") ? `https://${url}` : url;
  //     return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer">${strings.t_click_here}</a>`;
  //   });
  // };
  const processText = (content: string) => {
    // If content already contains HTML tags, return it as is
    if (/<[a-z][\s\S]*>/i.test(content)) {
      return content;
    }

    // Otherwise process URLs as before
    const urlRegex = /(?:https?:\/\/)?(?:www\.)?globalblue\.com(?:\/[^\s,]*)?/g;
    let processedText = content.replace(urlRegex, (url) => {
      const fullUrl = url.startsWith('http') ? url : `https://${url}`;
      return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer">${strings.t_click_here}</a>`;
    });

    // Replace the literal "\n" sequence with a <br /> tag
    processedText = processedText.replace(/\\n/g, '<br />');
    return processedText;
  };


  const getTextElement = () => {
    const weightClass = getWeightClass(weight);
    const Tag = tag || TEXT_TAGS.SPAN;

    const processedText = typeof text === "string" ? processText(text) : text;

    // Only look for string key if showStringKeys is true
    const stringKey = showStringKeys
      ? Object.entries(strings).find(([key, value]) => value === text)?.[0]
      : undefined;

    return React.createElement(
      Tag,
      {
        ...(id ? { id: id } : {}),
        tabIndex,
        className: classNames(weightClass, style),
        style: {
          fontSize: `${isMobile ? size - 2 : size}px`,
          cursor: (showStringKeys && stringKey) ? 'help' : 'inherit',
          '--show-string-keys': showStringKeys ? 'block' : 'none'
        } as React.CSSProperties,
        "aria-hidden": isAriaHidden,
        ...(announce
          ? {
            "aria-live": politeness,
            "aria-atomic": true,
            role: politeness === "assertive" ? "alert" : "status",
          }
          : {
            "aria-label": ariaLabel ? ariaLabel : text,
            "aria-live": politeness,
          }),
        ...(typeof text === "string"
          ? {
            dangerouslySetInnerHTML: { __html: processedText },
            ...(stringKey ? { "data-string-key": stringKey } : {})
          }
          : {}),
      },
      typeof text !== "string" ? processedText : null
    );
  };

  const getWeightClass = (weight: number) => {
    switch (weight) {
      case 700:
        return styles.titleBold;
      case 600:
        return styles.titleSemiBold;
      case 500:
        return styles.titleMedium;
      default:
        return styles.titleRegular;
    }
  };

  return getTextElement();
};

export default UIText;
